import { Folder, FileText } from 'lucide-react'
import { memo } from 'react'
import { createPortal } from 'react-dom'
import { FileExplorerItem, DragDropHandlers } from '@features/file-explorer/types'
import MimeTypeIcon from './Table/MimeTypeBadge'
import UserAvatar from '@components/UserAvatar'
import AvatarStack from '@components/AvatarStack'
import ItemActionsMenu from './ItemActionsMenu'
import ActionMenuItems from './ActionMenuItems'
import { useContextMenu } from '@hooks/useContextMenu'
import { useItemSelection } from '@hooks/useItemSelection'
import { useItemDrag } from '@features/file-operations/hooks/useItemDrag'
import { createItemClickHandler } from '@utils/itemClickHandlers'
import { formatFileSize } from '@utils/formatBytes'

interface GridCardProps {
  item: FileExplorerItem
  items: FileExplorerItem[]
  index: number
  isLoading: boolean
  dragAndDrop: DragDropHandlers
  onItemClick: (item: FileExplorerItem) => void
  onSelectItem: (item: FileExplorerItem, index: number, shiftKey: boolean) => void
  onDelete: (item: FileExplorerItem) => void
  onRename: (item: FileExplorerItem) => void
  onDownload: (item: FileExplorerItem) => void
  onShow: (item: FileExplorerItem) => void
  onManagePermissions: (item: FileExplorerItem) => void
  getItemKey: (item: FileExplorerItem) => string
}

function GridCard({
  item,
  items,
  index,
  isLoading,
  dragAndDrop,
  onItemClick,
  onSelectItem,
  onDelete,
  onRename,
  onDownload,
  onShow,
  onManagePermissions,
  getItemKey
}: GridCardProps) {
  // Use shared hooks
  const { contextMenu, contextMenuRef, handleContextMenu, closeContextMenu } = useContextMenu()
  const { itemKey: _itemKey, isSelected } = useItemSelection({ item, getItemKey })
  const { handleDragStart, isDraggingThisItem, isPartOfDrag, dragCount, folderDragHandlers } = useItemDrag({
    item,
    items,
    getItemKey,
    dragAndDrop
  })

  const handleCardClick = (e: React.MouseEvent) => {
    // Single click - select
    onSelectItem(item, index, e.shiftKey)
  }

  const renderFileIcon = (item: FileExplorerItem) => {
    const thumbnailClasses = "w-full h-32 rounded-t overflow-hidden"

    const avatarOverlay = (
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 rounded px-2 py-1">
        <div className="flex items-center gap-1">
          {item.owner && <UserAvatar user={item.owner} size="xs" />}
        </div>
        <div className="flex items-center">
          <AvatarStack users={item.shared_with} maxVisible={2} size="xs" />
        </div>
      </div>
    )

    if (item.is_folder) {
      return (
        <div className={`relative flex items-center justify-center bg-base-200 ${thumbnailClasses}`}>
          <Folder className="w-16 h-16 text-primary" />
          {avatarOverlay}
        </div>
      )
    }

    // If there's a preview URL, show the thumbnail
    if (item.preview_url) {
      return (
        <div className={`relative ${thumbnailClasses}`}>
          <img
            src={item.preview_url}
            alt={item.name}
            className="w-full h-32 object-cover"
          />
          {avatarOverlay}
        </div>
      )
    }

    // Fallback to icon for documents without preview
    return (
      <div className={`relative flex items-center justify-center bg-base-200 ${thumbnailClasses}`}>
        <FileText className="w-16 h-16 text-base-content/70" />
        {avatarOverlay}
      </div>
    )
  }

  return (
    <>
    <div
      key={`${item.type}-${item.id}`}
      draggable={true}
      className={`card bg-base-100 border shadow-sm cursor-pointer transition-all hover:shadow-md relative border-2 ${
        isSelected ? 'border-primary bg-primary/5' : 'border-base-300 border-transparent'
      } ${folderDragHandlers.isDragOverThisFolder ? 'border-primary border-4 bg-primary/20 ring-2 ring-primary scale-105' : ''} ${
        isLoading ? 'pointer-events-none opacity-70' : ''
      } ${isPartOfDrag ? 'opacity-50' : ''}`}
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      onDragEnd={dragAndDrop.handleItemDragEnd}
      onDragEnter={folderDragHandlers.handleDragEnter}
      onDragOver={folderDragHandlers.handleDragOver}
      onDragLeave={folderDragHandlers.handleDragLeave}
      onDrop={folderDragHandlers.handleDrop}
    >
      {/* Show count badge when dragging multiple items and this is the first one */}
      {isDraggingThisItem && dragCount > 1 && (
        <div className="absolute -top-2 -left-2 badge badge-primary badge-sm z-20">
          +{dragCount - 1}
        </div>
      )}

      {/* Actions Menu - positioned absolutely on card, not clipped */}
      <div className="absolute top-2 right-2 z-10">
        <ItemActionsMenu item={item} onDelete={onDelete} onRename={onRename} onDownload={onDownload} onShow={onShow} onManagePermissions={onManagePermissions} />
      </div>

      <a
        href={item.is_folder ? `/${item.id}` : `/documents/${item.id}/preview`}
        onClick={createItemClickHandler(item, onItemClick)}
        className="cursor-pointer relative overflow-hidden rounded-box block"
      >
        {renderFileIcon(item)}
      </a>
      <div className="card-body p-3">
        <a
          href={item.is_folder ? `/${item.id}` : `/documents/${item.id}/preview`}
          className="link link-hover text-sm font-medium truncate w-full text-left block"
          title={item.name}
          onClick={createItemClickHandler(item, onItemClick)}
        >
          {item.name}
        </a>
        <div className="flex items-center gap-2 text-xs text-base-content/70">
          {item.type === 'Document' && item.mime_type && (
            <MimeTypeIcon mimeType={item.mime_type} extension={item.extension} />
          )}
          <span>{formatFileSize(item.size, item.is_folder)}</span>
        </div>
      </div>
    </div>

    {/* Context Menu - rendered via portal to avoid z-index issues */}
    {contextMenu && createPortal(
      <div
        ref={contextMenuRef}
        className="fixed bg-base-100 rounded-box z-50 w-52 p-2 shadow-lg border border-base-300"
        style={{
          left: `${contextMenu.x}px`,
          top: `${contextMenu.y}px`,
        }}
      >
        <ul className="menu menu-sm p-0">
          <ActionMenuItems
            item={item}
            onDelete={onDelete}
            onRename={onRename}
            onDownload={onDownload}
            onShow={onShow}
            onManagePermissions={onManagePermissions}
            onActionClick={closeContextMenu}
          />
        </ul>
      </div>,
      document.body
    )}
    </>
  )
}

function arePropsEqual(prevProps: GridCardProps, nextProps: GridCardProps) {
  // Check if shared_with has changed
  const prevSharedWith = prevProps.item.shared_with
  const nextSharedWith = nextProps.item.shared_with
  const sharedWithChanged =
    prevSharedWith.length !== nextSharedWith.length ||
    prevSharedWith.some((user, i) => user.id !== nextSharedWith[i]?.id)

  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.item.updated_at === nextProps.item.updated_at &&
    !sharedWithChanged &&
    prevProps.dragAndDrop.isDragging === nextProps.dragAndDrop.isDragging &&
    prevProps.dragAndDrop.dragOverTarget === nextProps.dragAndDrop.dragOverTarget &&
    prevProps.dragAndDrop.draggedItem?.id === nextProps.dragAndDrop.draggedItem?.id
  )
}

export default memo(GridCard, arePropsEqual)
